/**
 * Scale Selector Component
 * Interactive scale slider with draggable pointer and image scaling
 * Web Component implementation
 */

class ScaleSelectorElement extends HTMLElement {
  constructor() {
    super();
    
    // State
    this.isDragging = false;
    this.startX = 0;
    this.startLeft = 0;
    this.positions = [];
    this.scalesData = [];
    this.currentIndex = 0;
  }

  connectedCallback() {
    // Parse data from attribute
    const dataAttr = this.getAttribute('data-scales');
    if (dataAttr) {
      try {
        this.scalesData = JSON.parse(dataAttr);
        this.currentIndex = Math.floor(this.scalesData.length / 2);
      } catch (e) {
        console.error('Scale Selector: Invalid data-scales attribute', e);
        return;
      }
    }

    // Get section ID from attribute
    this.sectionId = this.getAttribute('data-section-id');
    if (!this.sectionId) {
      console.error('Scale Selector: data-section-id attribute is required');
      return;
    }

    // Find DOM elements
    this.slider = this.querySelector(`#scale-slider-${this.sectionId}`);
    this.pointer = this.querySelector(`#scale-pointer-${this.sectionId}`);
    this.pointerText = this.querySelector(`#scale-pointer-text-${this.sectionId}`);
    this.productImage = this.querySelector(`#scale-product-image-${this.sectionId}`);
    this.button = this.querySelector(`#scale-button-${this.sectionId}`);
    this.sliderContainer = this.slider?.parentElement;

    if (!this.slider || !this.pointer) {
      console.warn('Scale Selector: Required elements not found');
      return;
    }

    this.scaleItems = Array.from(this.slider.querySelectorAll('.scale-selector__scale-item'));
    
    // Wait for images and CSS to load before initializing
    this.waitForLayout().then(() => {
      this.init();
    });
  }

  // Wait for layout to be ready (images loaded, CSS applied)
  async waitForLayout() {
    // Wait for images to load
    const images = Array.from(this.querySelectorAll('img'));
    await Promise.all(images.map(img => {
      if (img.complete) return Promise.resolve();
      return new Promise(resolve => {
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
      });
    }));

    // Wait for next animation frame to ensure CSS is applied
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));
  }

  init() {
    this.isMobile = window.innerWidth <= 768;
    
    // Calculate positions
    this.calculatePositions();
    
    // Verify positions were calculated correctly
    if (this.positions.length === 0) {
      console.error('Scale Selector: Failed to calculate positions');
      return;
    }
    
    if (this.isMobile) {
      // On mobile, scroll to center item
      this.scrollToIndex(this.currentIndex, false);
    } else {
      // On desktop, position pointer
      this.updateToCurrentIndex(false);
    }
    
    this.setupEventListeners();

    // Recalculate on window resize
    this.resizeHandler = this.debounce(() => {
      const wasMobile = this.isMobile;
      this.isMobile = window.innerWidth <= 768;
      
      this.calculatePositions();
      
      // Only update if we have valid positions
      if (this.positions.length > 0) {
        if (this.isMobile) {
          this.scrollToIndex(this.currentIndex, false);
        } else {
          this.updateToCurrentIndex(false);
        }
      }
    }, 250);
    
    window.addEventListener('resize', this.resizeHandler);
  }

  disconnectedCallback() {
    // Clean up event listeners
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  calculatePositions() {
    if (!this.slider) {
      console.error('Scale Selector: slider element not found');
      return;
    }
    
    const sliderRect = this.slider.getBoundingClientRect();
    
    // Validate slider has dimensions
    if (sliderRect.width === 0 || sliderRect.height === 0) {
      console.error('Scale Selector: slider has no dimensions', sliderRect);
      this.positions = [];
      return;
    }
    
    const sliderLeft = sliderRect.left;
    
    // Calculate positions based on actual element positions (accounts for gaps)
    this.positions = this.scaleItems.map((item, index) => {
      const itemRect = item.getBoundingClientRect();
      
      // Validate item has dimensions
      if (itemRect.width === 0) {
        console.warn('Scale Selector: scale item has no width', item);
      }
      
      // Get center of each item
      const centerX = itemRect.left + (itemRect.width / 2) - sliderLeft;
      // Calculate percentage relative to slider width
      const percentage = (centerX / sliderRect.width) * 100;
      
      return {
        x: centerX,
        percentage: percentage,
        element: item,
        value: item.dataset.scale,
        url: item.dataset.url,
        index: parseInt(item.dataset.index)
      };
    });
    
    // Log for debugging
    console.log('Scale Selector: Positions calculated', {
      sliderWidth: sliderRect.width,
      itemCount: this.scaleItems.length,
      positionsCount: this.positions.length
    });
  }

  setupEventListeners() {
    if (this.isMobile) {
      // On mobile, listen to scroll events on the container
      this.sliderContainer.addEventListener('scroll', this.debounce(() => {
        this.updateFromScroll();
      }, 100));
    } else {
      // Desktop: enable drag
      // Mouse events
      this.pointer.addEventListener('mousedown', this.onDragStart.bind(this));
      document.addEventListener('mousemove', this.onDragMove.bind(this));
      document.addEventListener('mouseup', this.onDragEnd.bind(this));
      
      // Touch events
      this.pointer.addEventListener('touchstart', this.onDragStart.bind(this), { passive: false });
      document.addEventListener('touchmove', this.onDragMove.bind(this), { passive: false });
      document.addEventListener('touchend', this.onDragEnd.bind(this));
    }
  }

  onDragStart(e) {
    e.preventDefault();
    this.isDragging = true;
    this.pointer.classList.add('is-dragging');
    
    // Disable transitions for immediate response
    this.pointer.style.transition = 'none';
    if (this.productImage) {
      this.productImage.style.transition = 'none';
    }
    
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const pointerRect = this.pointer.getBoundingClientRect();
    
    this.startX = clientX;
    this.startLeft = pointerRect.left - this.slider.getBoundingClientRect().left + (pointerRect.width / 2);
  }

  onDragMove(e) {
    if (!this.isDragging) return;
    
    e.preventDefault();
    
    // Use requestAnimationFrame for smoother updates
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
    }
    
    this.rafId = requestAnimationFrame(() => {
      const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
      const deltaX = clientX - this.startX;
      let newLeft = this.startLeft + deltaX;
      
      // Constrain to slider bounds
      const minLeft = this.positions[0].x;
      const maxLeft = this.positions[this.positions.length - 1].x;
      newLeft = Math.max(minLeft, Math.min(maxLeft, newLeft));
      
      // Calculate percentage based on position
      const sliderWidth = this.slider.offsetWidth;
      const percentage = (newLeft / sliderWidth) * 100;
      
      // Update pointer position immediately using percentage
      this.pointer.style.left = `${percentage}%`;
      
      // Calculate scale based on position (smooth scaling)
      const progress = (newLeft - minLeft) / (maxLeft - minLeft);
      const scaleIndex = progress * (this.scalesData.length - 1);
      this.updateImageScale(scaleIndex);
      
      // Scroll container on mobile if needed
      if (window.innerWidth <= 768 && this.sliderContainer) {
        const containerRect = this.sliderContainer.getBoundingClientRect();
        const pointerX = clientX - containerRect.left;
        
        if (pointerX < 100) {
          this.sliderContainer.scrollLeft -= 10;
        } else if (pointerX > containerRect.width - 100) {
          this.sliderContainer.scrollLeft += 10;
        }
      }
    });
  }

  onDragEnd(e) {
    if (!this.isDragging) return;
    
    this.isDragging = false;
    this.pointer.classList.remove('is-dragging');
    
    // Cancel any pending animation frame
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    
    // Re-enable transitions for snap animation
    this.pointer.style.transition = '';
    if (this.productImage) {
      this.productImage.style.transition = '';
    }
    
    // Find nearest position and snap
    const pointerRect = this.pointer.getBoundingClientRect();
    const pointerCenter = pointerRect.left - this.slider.getBoundingClientRect().left + (pointerRect.width / 2);
    
    let nearestIndex = 0;
    let nearestDistance = Infinity;
    
    this.positions.forEach((pos, index) => {
      const distance = Math.abs(pos.x - pointerCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    });
    
    this.currentIndex = nearestIndex;
    this.updateToCurrentIndex(true);
  }

  updateToCurrentIndex(animate = true) {
    if (!this.positions[this.currentIndex]) return;
    
    const targetPos = this.positions[this.currentIndex];
    
    // Update pointer position using percentage (desktop only)
    if (!this.isMobile) {
      if (animate) {
        this.pointer.style.transition = 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1)';
      } else {
        this.pointer.style.transition = 'none';
      }
      
      // Use percentage for consistent positioning
      this.pointer.style.left = `${targetPos.percentage}%`;
    }
    
    // Update pointer text
    if (this.pointerText) {
      this.pointerText.textContent = targetPos.value;
    }
    
    // Update button URL
    if (this.button && targetPos.url) {
      this.button.href = targetPos.url;
    }
    
    // Update image scale
    this.updateImageScale(this.currentIndex);
  }

  updateImageScale(scaleIndex) {
    if (!this.productImage || !this.scalesData.length) return;
    
    // Interpolate between scales
    const floorIndex = Math.floor(scaleIndex);
    const ceilIndex = Math.ceil(scaleIndex);
    const fraction = scaleIndex - floorIndex;
    
    const floorScale = this.scalesData[floorIndex]?.scale || 1;
    const ceilScale = this.scalesData[ceilIndex]?.scale || floorScale;
    
    let interpolatedScale = floorScale + (ceilScale - floorScale) * fraction;
    
    // On mobile, divide scale by 1.5 to prevent image from being cut off
    if (this.isMobile) {
      interpolatedScale = interpolatedScale / 1.5;
    }
    
    // Use will-change for better performance during drag
    if (this.isDragging) {
      this.productImage.style.willChange = 'transform';
    } else {
      this.productImage.style.willChange = 'auto';
    }
    
    this.productImage.style.transform = `scale(${interpolatedScale})`;
  }

  // Mobile: Scroll to specific index
  scrollToIndex(index, animate = true) {
    if (!this.scaleItems[index] || !this.sliderContainer) return;
    
    const targetItem = this.scaleItems[index];
    const containerRect = this.sliderContainer.getBoundingClientRect();
    const itemRect = targetItem.getBoundingClientRect();
    
    // Calculate scroll position to center the item
    const scrollLeft = this.sliderContainer.scrollLeft + 
                      (itemRect.left - containerRect.left) - 
                      (containerRect.width / 2) + 
                      (itemRect.width / 2);
    
    this.sliderContainer.scrollTo({
      left: scrollLeft,
      behavior: animate ? 'smooth' : 'auto'
    });
  }

  // Mobile: Update when user scrolls
  updateFromScroll() {
    if (!this.isMobile || !this.sliderContainer) return;
    
    const containerRect = this.sliderContainer.getBoundingClientRect();
    const containerCenter = containerRect.left + (containerRect.width / 2);
    
    // Find which scale item is closest to center
    let closestIndex = 0;
    let closestDistance = Infinity;
    
    this.scaleItems.forEach((item, index) => {
      const itemRect = item.getBoundingClientRect();
      const itemCenter = itemRect.left + (itemRect.width / 2);
      const distance = Math.abs(itemCenter - containerCenter);
      
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    
    // Update current index and UI
    if (closestIndex !== this.currentIndex) {
      this.currentIndex = closestIndex;
      
      // Update pointer text
      if (this.pointerText && this.scalesData[closestIndex]) {
        this.pointerText.textContent = this.scalesData[closestIndex].value;
      }
      
      // Update button URL
      if (this.button && this.scalesData[closestIndex]?.url) {
        this.button.href = this.scalesData[closestIndex].url;
      }
      
      // Update image scale
      this.updateImageScale(closestIndex);
    }
  }

  // Utility: Debounce function
  debounce(func, wait) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(this, args), wait);
    };
  }
}

// Define the custom element
if (!customElements.get('scale-selector')) {
  customElements.define('scale-selector', ScaleSelectorElement);
}
